import type { ElementRef, Signal, OutputEmitterRef, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { map, pairwise } from 'rxjs';

import { SpinnerComponent } from '@core/user-interface-legacy';
import { MaxFileSize, validateMaxFileSize } from '@core/utils-legacy';

enum State {
  Empty = 'empty',
  DragOver = 'over',
  Uploaded = 'uploaded',
  Error = 'error',
  Uploading = 'uploading',
}

export interface UploadFileBoxI {
  accept: Signal<string>;
  isUploading: Signal<boolean>;
  error: Signal<string | undefined>;
  savedFile: Signal<string | undefined>;
  fileSelected: OutputEmitterRef<File>;
  filesSelected?: OutputEmitterRef<File[]>;
}

@Component({
  selector: 'tl-upload-file-box',
  templateUrl: './upload-file-box.component.html',
  styleUrl: './upload-file-box.component.less',
  host: {
    '[class.empty]': 'state() === State.Empty',
    '[class.over]': 'state() === State.DragOver',
    '[class.uploaded]': 'state() === State.Uploaded',
    '[class.uploading]': 'isUploading()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SpinnerComponent],
})
export class UploadFileBoxComponent implements UploadFileBoxI {
  public readonly State = State;

  public inputEl = viewChild.required<ElementRef>('fileInput');
  public accept = input.required<string>();
  public savedFile = input.required<string | undefined>();
  public error = input.required<string | undefined>();
  public isUploading = input.required<boolean>();
  public maxFileSize = input<MaxFileSize>(MaxFileSize.File15MB);
  public multiple = input(false);

  public fileSelected = output<File>();
  public filesSelected = output<File[]>();
  public validationError = output<{
    typeError: boolean;
    sizeError: boolean;
  }>();

  public state = signal(State.DragOver);
  public prevState = toSignal(
    toObservable(this.state).pipe(
      pairwise(),
      map(([prev]) => prev),
    ),
    { initialValue: this.state() },
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (this.error()) {
      this.state.set(State.Error);
    } else {
      if ('savedFile' in changes && changes.savedFile.isFirstChange()) {
        this.savedFile() ? this.state.set(State.Uploaded) : this.state.set(State.Empty);
      }

      if ('isUploading' in changes && !changes.isUploading.isFirstChange()) {
        this.isUploading()
          ? this.state.set(State.Uploading)
          : this.state.set(this.savedFile() ? State.Uploaded : State.Empty);
      }
    }
  }

  public get file() {
    const files = this.inputEl().nativeElement.files;
    return files?.length === 1 ? files[0] : null;
  }

  public onDragOver() {
    this.state.set(State.DragOver);
  }

  public onDragLeave() {
    this.state.set(this.prevState()!);
  }

  public handleChange(files: File | File[] | null) {
    if (!files) return;

    if (Array.isArray(files)) {
      const firstError = files.map(f => this.getValidationError(f)).find(Boolean);
      if (firstError) {
        this.validationError.emit(firstError);
        this.state.set(this.prevState()!);
      } else {
        this.filesSelected.emit(files);
      }
    } else {
      const error = this.getValidationError(files);
      if (error) {
        this.validationError.emit(error);
        this.state.set(this.prevState()!);
      } else {
        this.fileSelected.emit(files);
      }
    }

    this.clearInput();
  }

  public getFileFromEvent(event: Event): File | File[] | null {
    const target = <HTMLInputElement>event.target;
    if (!target?.files?.length) return null;
    return this.multiple() ? Array.from(target.files) : target.files[0];
  }

  public openFilePicker() {
    this.inputEl().nativeElement.click();
  }

  private clearInput() {
    this.inputEl().nativeElement.value = null;
  }

  private getValidationError(file: File): { typeError: boolean; sizeError: boolean } | null {
    const typeValid = this.isTypeValid(file);
    const sizeValid = validateMaxFileSize(file, this.maxFileSize());
    return !typeValid || !sizeValid ? { typeError: !typeValid, sizeError: !sizeValid } : null;
  }

  private isTypeValid(file: File): boolean {
    const tokens = this.accept()
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) return true;

    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';

    return tokens.some(token => {
      if (token.startsWith('.')) return ext === token.toLowerCase();
      if (token.endsWith('/*')) return file.type !== '' && file.type.startsWith(token.slice(0, -1));
      return file.type !== '' && file.type === token;
    });
  }
}
